import { useEffect, useRef, useState } from "react";
import { ProductCategory } from "@/types/product";
import { categoriesFetch } from "@/apis/product";
import { searchTreeList } from "@/pages/OrgManager/utils/searchTree";
import { notification } from "antd";
import errorCodes from "@/apis/error";
import { useTranslation } from "react-i18next";
import { CommonListParams } from "@/types/common";

type IFetchsFunc = () => void;
type IAddFunc = (info: ProductCategory) => void;
type IUpdateFunc = (info: ProductCategory) => void;
type IDeleteFunc = (item: ProductCategory) => void;
type ISearchFunc = (keyword: string) => void;

type IUseCategories = (props: {
  keyword?: string;
  cateOrgPermission?: boolean;
}) => [
    Array<ProductCategory>,
    number,
    boolean,
    { addCategory: IAddFunc; updateCategory: IUpdateFunc, deleteCategory: IDeleteFunc, searchCategory: ISearchFunc, fetchs: IFetchsFunc }
  ];

const useCategories: IUseCategories = (props) => {
  const { t } = useTranslation();
  const { keyword, cateOrgPermission } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [list, setList] = useState<Array<ProductCategory>>([]);
  const [total, setTotal] = useState<number>(0);
  const [backupList, setBackupList] = useState<Array<ProductCategory>>([]);
  const isUpdateBackupList = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    fetchs()
  }, [keyword, cateOrgPermission]);

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (isUpdateBackupList.current) setBackupList(list)
  }, [list])

  const sortList = (originList: ProductCategory[]) => {
    let sortedList = originList.sort((a, b) => Number(a.code) - Number(b.code))
    for (let i = 0; i < sortedList.length; i++) {
      if (sortedList[i].subs && sortedList[i].subs.length > 0) sortedList[i].subs = sortList(sortedList[i].subs)
    }
    return sortedList
  }

  const fetchs: IFetchsFunc = () => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    const params: CommonListParams = {
      body: keyword,
      controller, // ✅ 传递 controller 用于取消请求
    };

    categoriesFetch(params, cateOrgPermission)
      .then((res) => {
        let responseList = res.body.filter(item => item.pId === null || !item.pId)

        setList(sortList(responseList));
      })
      .catch((err) => {
        // ✅ 忽略取消的请求
        if (err?.name === 'AbortError') {
          return;
        }
        notification.warning({ placement: 'top', message: t('category.fetchFail'), description: errorCodes[err] || err })
      })
      .finally(() => setLoading(false))
  }

  const addCategory: IAddFunc = (item) => {
    isUpdateBackupList.current = true
    setLoading(true)
    if (item.pId) {
      setList(TreeNodeAction('add', item, item.pId))
    } else {
      setList([item, ...list]);
      setTotal(total + 1);
    }
    setLoading(false)
  };

  const updateCategory: IUpdateFunc = (upItem) => {
    isUpdateBackupList.current = true
    setLoading(true)
    if (upItem.pId) {
      setList(TreeNodeAction('update', upItem, upItem.pId))
    } else {
      setList((prev) => {
        return prev?.map((item) => (item.id === upItem.id ? upItem : item));
      });
    }
    setLoading(false)
  };

  const deleteCategory: IDeleteFunc = (delItem) => {
    isUpdateBackupList.current = true
    setLoading(true)
    if (delItem.pId) {
      setList(TreeNodeAction('delete', delItem, delItem.pId))
    } else {
      setList(list.filter(item => item.id !== delItem.id));
    }
    setLoading(false)
  }

  const TreeNodeAction = (opration: 'add' | 'update' | 'delete', item: ProductCategory, parentId: string): ProductCategory[] => {
    const newList = [...list];
    const modifyNode = (nodes: ProductCategory[]) => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === parentId) {
          switch (opration) {
            case 'add':
              if (!nodes[i].subs) nodes[i].subs = [];
              nodes[i].subs.push(item);
              break;
            case 'update':
              if (nodes[i].subs) {
                for (let j = 0; j < nodes[i].subs.length; j++) {
                  if (nodes[i].subs[j].id === item.id) {
                    nodes[i].subs[j] = item
                    return;
                  }
                }
              }
              break;
            case 'delete':
              if (nodes[i].subs) {
                nodes[i].subs = nodes[i].subs.filter((node) => node.id !== item.id);
                return
              }
              break;
          }
        } else {
          if (nodes[i].subs) modifyNode(nodes[i].subs);
        }
      }
    }
    modifyNode(newList)
    return newList;
  }

  const searchCategory: ISearchFunc = (keyword) => {
    setLoading(true)
    const keywordVal = keyword.trim()
    if (keywordVal && keywordVal.length > 0) {
      isUpdateBackupList.current = false
      setList(searchTreeList(backupList, keywordVal))
    } else {
      isUpdateBackupList.current = true
      fetchs()
    }
    setLoading(false)
  }

  return [list, total, loading, { addCategory, updateCategory, deleteCategory, searchCategory, fetchs }];
};

export default useCategories;
